'use client'

import { useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { ArrowRight, Check, ChevronDown, Mail, Menu, X } from 'lucide-react'
import BackToTop from './components/BackToTop'
import WhatsAppFloatButton from './components/WhatsAppFloatButton'
import RevealObserver from './components/RevealObserver'
import { captureLeadSource } from './lib/leadSource'
import { flushLeadOutbox } from './lib/leadCapture'
import './App.css'
import './styles/service-detail.css'
import './styles/site-nav.css'
import { SERVICE_MENU } from './data/serviceCatalog'
import {
  AUDIENCE_DOORS,
  COMPANY_LINKS,
  HIRE_SPECIALIST,
  INDUSTRIES,
  NAV_SECTIONS,
  RESULTS_LINKS,
  isSectionActive,
} from './data/siteNav'

// Organization / WebSite / founder JSON-LD now lives in ONE @graph emitted by
// the root layout (src/app/layout.tsx via src/lib/schema.ts siteGraph()).

// Route-level code splitting is now handled by the Next.js App Router (each
// app/**/page.tsx imports its own page component), so the React.lazy() list
// that used to live here is gone. Page components themselves are unchanged.

// Same three promises as the homepage hero trust row (HeroV2.jsx).
const SERVICES_PROMISES = ['GoHighLevel Certified Admins', 'White-labeled under your brand', '24/7 coverage']

function NavItem({ item, pathname, prefetch }) {
  const active = pathname === item.to
  return (
    <Link href={item.to} prefetch={prefetch} className={`nav-item${active ? ' is-active' : ''}`} aria-current={active ? 'page' : undefined}>
      <span className="nav-item-label">
        {item.label}
        <ArrowRight size={13} className="nav-item-arrow" aria-hidden="true" />
      </span>
      {item.description ? <span className="nav-item-desc">{item.description}</span> : null}
    </Link>
  )
}

function WhoWeHelpPanel({ pathname, prefetch }) {
  return (
    <div className="nav-who">
      <div className="nav-who-roles">
        {AUDIENCE_DOORS.map((door) => (
          <Link
            key={door.to}
            href={door.to}
            prefetch={prefetch}
            className={`nav-door${door.featured ? ' is-featured' : ''}`}
            aria-current={pathname === door.to ? 'page' : undefined}
          >
            <span className="nav-door-icon" aria-hidden="true"><door.icon size={20} /></span>
            <span className="nav-door-body">
              <span className="nav-door-label">{door.label}</span>
              <span className="nav-door-desc">{door.description}</span>
              <span className="nav-door-tag">{door.tag}</span>
            </span>
            <ArrowRight size={18} className="nav-door-arrow" aria-hidden="true" />
          </Link>
        ))}
        <NavItem item={HIRE_SPECIALIST} pathname={pathname} prefetch={prefetch} />
      </div>
      <div className="nav-who-industries">
        <span className="nav-col-title">By Industry</span>
        <span className="nav-col-desc">GoHighLevel set up for how your field works</span>
        <ul className="nav-industry-list">
          {INDUSTRIES.map((item) => (
            <li key={item.to}>
              <Link href={item.to} prefetch={prefetch} className={`nav-industry${pathname === item.to ? ' is-active' : ''}`}>
                {item.label}
                <ArrowRight size={14} aria-hidden="true" />
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}

function ServicesPanel({ pathname, prefetch }) {
  return (
    <div className="nav-mega">
      <div className="nav-mega-cols">
        {SERVICE_MENU.map((col) => (
          <div key={col.category} className="nav-col">
            <div className="nav-col-head">
              <span className="nav-col-title">{col.category}</span>
              <span className="nav-col-desc">{col.description}</span>
            </div>
            <ul className="nav-list">
              {col.items.map((item) => (
                <li key={item.to}><NavItem item={item} pathname={pathname} prefetch={prefetch} /></li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <aside className="nav-feature">
        <div className="nav-feature-intro">
          <p className="nav-feature-title">Not sure which service you need?</p>
          <p className="nav-feature-text">
            Tell us what&rsquo;s slowing your business down. We&rsquo;ll point you to the right fix on a free call.
          </p>
        </div>
        <ul className="nav-feature-checks">
          {SERVICES_PROMISES.map((text) => (
            <li key={text}><Check size={14} aria-hidden="true" /> {text}</li>
          ))}
        </ul>
        <div className="nav-feature-actions">
          <Link href="/booking" prefetch={prefetch} className="nav-feature-cta">
            Book a free call <ArrowRight size={15} aria-hidden="true" />
          </Link>
          <Link href="/services" prefetch={prefetch} className="nav-feature-link">Browse all services</Link>
        </div>
      </aside>
    </div>
  )
}

function LinkListPanel({ items, pathname, prefetch, columns = 1 }) {
  return (
    <ul className={`nav-list${columns > 1 ? ' is-two-col' : ''}`}>
      {items.map((item) => (
        <li key={item.to}><NavItem item={item} pathname={pathname} prefetch={prefetch} /></li>
      ))}
    </ul>
  )
}

// Desktop panel per section. Results and Company are short lists, so they hang
// under their own trigger (`anchored`); the two wide panels centre on the bar.
//
// The panels are in the HTML on every page (crawlers follow their links), so
// a closed panel's links count as "in the viewport" and Next would prefetch
// all ~35 routes on page load, competing with the page's own LCP. Links in a
// closed panel get prefetch={false}; opening the panel restores the default,
// and Next then prefetches that panel's links.
const DESKTOP_PANELS = {
  who: { anchored: false, render: (pathname, prefetch) => <WhoWeHelpPanel pathname={pathname} prefetch={prefetch} /> },
  services: { anchored: false, render: (pathname, prefetch) => <ServicesPanel pathname={pathname} prefetch={prefetch} /> },
  results: { anchored: true, render: (pathname, prefetch) => <LinkListPanel items={RESULTS_LINKS} pathname={pathname} prefetch={prefetch} /> },
  company: { anchored: true, render: (pathname, prefetch) => <LinkListPanel items={COMPANY_LINKS} pathname={pathname} prefetch={prefetch} columns={2} /> },
}

function SiteHeader() {
  const pathname = usePathname()
  const isAuthLayout = pathname === '/login' || pathname.startsWith('/admin')
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [mobileSection, setMobileSection] = useState(null)
  // Which desktop dropdown is open (a NAV_SECTIONS key), or null.
  const [openKey, setOpenKey] = useState(null)
  const closeTimer = useRef(null)
  // A hover opens the menu; a click right after shouldn't then close it.
  const openedByHover = useRef(false)
  const triggerRefs = useRef({})

  const cancelClose = () => {
    if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null }
  }
  const openOnHover = (key) => {
    cancelClose()
    if (openKey !== key) openedByHover.current = true
    setOpenKey(key)
  }
  // Short delay so the pointer can cross the gap between trigger and panel.
  const closeSoon = () => {
    cancelClose()
    closeTimer.current = setTimeout(() => setOpenKey(null), 200)
  }
  const toggleOnClick = (key) => {
    cancelClose()
    if (openKey === key && !openedByHover.current) setOpenKey(null)
    else setOpenKey(key)
    openedByHover.current = false
  }

  const [prevPathname, setPrevPathname] = useState(pathname)
  if (pathname !== prevPathname) {
    setPrevPathname(pathname)
    setMobileMenuOpen(false)
    setMobileSection(null)
    setOpenKey(null)
  }

  // Escape closes the open dropdown and returns focus to its trigger; a click
  // anywhere outside the header closes it too.
  useEffect(() => {
    if (!openKey) return undefined
    const onKey = (event) => {
      if (event.key !== 'Escape') return
      triggerRefs.current[openKey]?.focus()
      setOpenKey(null)
    }
    const onPointer = (event) => {
      if (!event.target.closest('.navbar')) setOpenKey(null)
    }
    document.addEventListener('keydown', onKey)
    document.addEventListener('pointerdown', onPointer)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('pointerdown', onPointer)
    }
  }, [openKey])

  // Compacts the bar once the page has moved. Threshold is well past the
  // .85rem top margin so the transition can't oscillate at rest.
  const [scrolled, setScrolled] = useState(false)
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  if (isAuthLayout) return null

  const closeMobile = () => setMobileMenuOpen(false)

  return (
    <header className={`navbar container${scrolled ? ' is-scrolled' : ''}`}>
      <div className="brand">
        {/* No point prefetching the homepage from the homepage. */}
        <Link href="/" prefetch={pathname === '/' ? false : undefined}>
          <img className="brand-logo" src="/ghl-prime-logo-nav-h96.webp" alt="GHL Prime" width="288" height="96" fetchPriority="high" decoding="async" />
        </Link>
      </div>

      <nav className="nav-links desktop-nav-links" aria-label="Main">
        {NAV_SECTIONS.map((section) => {
          const open = openKey === section.key
          const panel = DESKTOP_PANELS[section.key]
          return (
            <div
              key={section.key}
              className={`nav-menu${panel.anchored ? ' is-anchored' : ''}`}
              data-open={open}
              onBlur={(event) => {
                // Keyboard users tabbing out of a menu close it.
                if (open && !event.currentTarget.contains(event.relatedTarget)) setOpenKey(null)
              }}
            >
              {/* Hover handlers live on the trigger and its panel only, never
                  on this wrapper: the wide panels are positioned against the
                  navbar, outside the wrapper's box, so the wrapper's own size
                  must never decide whether the pointer is "still hovering". */}
              <button
                type="button"
                ref={(el) => { triggerRefs.current[section.key] = el }}
                className={`nav-trigger${isSectionActive(section, pathname) ? ' is-active' : ''}`}
                aria-expanded={open}
                aria-controls={`nav-panel-${section.key}`}
                onMouseEnter={() => openOnHover(section.key)}
                onMouseLeave={closeSoon}
                onClick={() => toggleOnClick(section.key)}
              >
                {section.label} <ChevronDown size={15} aria-hidden="true" />
              </button>
              <div
                id={`nav-panel-${section.key}`}
                className={`nav-panel is-${section.key}`}
                onMouseEnter={cancelClose}
                onMouseLeave={closeSoon}
                // `inert` removes the closed panel from hit-testing, focus and
                // the accessibility tree, independent of the CSS fade state.
                inert={!open}
              >
                {panel.render(pathname, open ? null : false)}
              </div>
            </div>
          )
        })}
      </nav>

      <div className="nav-actions desktop-nav-actions">
        <Link href="/contact" className="login-link nav-ghost">
          <Mail size={15} aria-hidden="true" />
          Contact
        </Link>
        <Link href="/booking" className="primary-pill nav-cta">
          Get a free consultation
          <ArrowRight size={15} aria-hidden="true" />
        </Link>
      </div>

      <button type="button" className="mobile-menu-toggle" onClick={() => setMobileMenuOpen((current) => !current)} aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'} aria-expanded={mobileMenuOpen}>
        {mobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
      </button>

      {mobileMenuOpen ? (
        <div className="mobile-menu-panel">
          <nav className="mobile-menu-links" aria-label="Main">
            {NAV_SECTIONS.map((section) => {
              const open = mobileSection === section.key
              return (
                <div key={section.key} className="mobile-nav-section">
                  <button
                    type="button"
                    className={`mobile-nav-toggle${isSectionActive(section, pathname) ? ' is-active' : ''}`}
                    data-open={open}
                    aria-expanded={open}
                    onClick={() => setMobileSection(open ? null : section.key)}
                  >
                    {section.label} <ChevronDown size={18} aria-hidden="true" />
                  </button>
                  {open ? (
                    <div className="mobile-nav-panel">
                      {section.allLink ? (
                        <Link href={section.allLink.to} className="mobile-nav-all" onClick={closeMobile}>{section.allLink.label}</Link>
                      ) : null}
                      {section.groups.map((group, index) => (
                        <div key={group.heading ?? index} className="mobile-nav-group">
                          {group.heading ? <div className="mobile-nav-heading">{group.heading}</div> : null}
                          {group.items.map((item) => (
                            <Link
                              key={item.to}
                              href={item.to}
                              className={`mobile-nav-link${pathname === item.to ? ' is-active' : ''}`}
                              onClick={closeMobile}
                            >
                              {item.label}
                            </Link>
                          ))}
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
              )
            })}
          </nav>
          <div className="mobile-menu-actions">
            <Link href="/contact" className="login-link nav-ghost" onClick={closeMobile}>
              <Mail size={16} aria-hidden="true" />
              Contact
            </Link>
            <Link href="/booking" className="primary-pill nav-cta" onClick={closeMobile}>
              Get a free consultation
              <ArrowRight size={16} aria-hidden="true" />
            </Link>
          </div>
        </div>
      ) : null}
    </header>
  )
}

function ScrollToTop() {
  const pathname = usePathname()

  useEffect(() => {
    // react-router exposed the hash via useLocation(); next/navigation has no
    // hash accessor, so it is read straight off the URL. Same guard, same
    // behaviour: an in-page anchor navigation must not be yanked to the top.
    if (typeof window !== 'undefined' && window.location.hash) return
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
  }, [pathname])

  return null
}

export default function App({ children }) {
  // Saves UTM tags and the referring site for the lead forms
  // (lib/leadSource.js), on the first load and on every route change.
  const pathname = usePathname()
  useEffect(() => {
    captureLeadSource()
  }, [pathname])

  // Hands any lead the backend missed (it was unreachable at submit time) to
  // the backend now, a few seconds after load (lib/leadCapture.js).
  useEffect(() => {
    const timer = setTimeout(() => { flushLeadOutbox() }, 4000)
    return () => clearTimeout(timer)
  }, [])

  // The GoHighLevel chat widget is heavy third-party JavaScript. It used to
  // load during the first paint and competed with the page for the main
  // thread; now it loads on the visitor's first interaction, or 8 s after the
  // page has loaded, whichever comes first.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined
    if (/HeadlessChrome|Puppeteer|prerender/i.test(navigator.userAgent)) return undefined
    const EVENTS = ['pointerdown', 'keydown', 'scroll', 'touchstart']
    let timer
    const load = () => {
      cleanup()
      if (document.getElementById('ghl-chat-widget-loader')) return
      const s = document.createElement('script')
      s.id = 'ghl-chat-widget-loader'
      s.src = 'https://widgets.leadconnectorhq.com/loader.js'
      s.setAttribute('data-resources-url', 'https://widgets.leadconnectorhq.com/chat-widget/loader.js')
      s.setAttribute('data-widget-id', '69cad06dae18f7331e86d6ad')
      s.async = true
      document.body.appendChild(s)
    }
    const startTimer = () => { timer = setTimeout(load, 8000) }
    function cleanup() {
      clearTimeout(timer)
      window.removeEventListener('load', startTimer)
      EVENTS.forEach((e) => window.removeEventListener(e, load))
    }
    EVENTS.forEach((e) => window.addEventListener(e, load, { once: true, passive: true }))
    if (document.readyState === 'complete') startTimer()
    else window.addEventListener('load', startTimer, { once: true })
    return cleanup
  }, [])

  return (
    <div className="site-shell">
      <div className="page-grid" />
      <div className="hero-glow top-left" />
      <div className="hero-glow bottom-right" />
      <SiteHeader />
      <ScrollToTop />
      <BackToTop />
      <WhatsAppFloatButton />
      <RevealObserver />
      <main>{children}</main>
    </div>
  )
}
